import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * 日本語入力の変換確定を送信と誤認しないための共通処理。
 *
 * IMEの変換中に押した Enter は「変換の確定」であって送信ではない。
 * React の onKeyDown では nativeEvent.isComposing（環境によっては keyCode 229）で
 * 変換中かどうかを判定できる。これを見ないと、変換を確定した瞬間に登録されてしまう。
 */
function isImeEnter(e: React.KeyboardEvent): boolean {
  return e.nativeEvent.isComposing || (e as unknown as { keyCode: number }).keyCode === 229;
}

/**
 * 追加用の入力欄。フォーカスが外れた時点で登録する。
 * Enter でも登録するが、変換確定のEnterは無視する。
 * ボタン押下と blur が二重に走っても1回しか登録しない。
 */
export function AddInput({
  placeholder,
  buttonLabel = '＋ 追加',
  onAdd,
  maxLength = 60,
  className = 'area-input',
  children,
  onReset,
}: {
  placeholder: string;
  buttonLabel?: string;
  onAdd: (value: string) => void | Promise<void>;
  maxLength?: number;
  className?: string;
  /** 入力欄とボタンの間に差し込む要素（期日など）。 */
  children?: ReactNode;
  /** 登録後に呼ばれる。付随する入力（期日など）を消すのに使う。 */
  onReset?: () => void;
}) {
  const [draft, setDraft] = useState('');
  const busy = useRef(false);

  const commit = () => {
    const value = draft.trim();
    if (value === '' || busy.current) return;
    busy.current = true;
    setDraft('');
    void Promise.resolve(onAdd(value)).finally(() => {
      onReset?.();
      // 同じ操作で blur とクリックが続けて起きても二重登録しない
      setTimeout(() => {
        busy.current = false;
      }, 0);
    });
  };

  return (
    <div className="btn-row">
      <input
        type="text"
        className={className}
        placeholder={placeholder}
        maxLength={maxLength}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !isImeEnter(e)) {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') setDraft('');
        }}
      />
      {children}
      <button className="ghost-btn" disabled={draft.trim() === ''} onMouseDown={(e) => e.preventDefault()} onClick={commit}>
        {buttonLabel}
      </button>
    </div>
  );
}

/**
 * その場編集用の入力欄。フォーカスが外れた時点で確定する。
 * Enter でも確定するが、変換確定のEnterは無視する。Escape で元に戻す。
 */
export function CommitInput({
  value,
  onCommit,
  className,
  placeholder,
  maxLength = 200,
}: {
  value: string;
  onCommit: (value: string) => void;
  className?: string;
  placeholder?: string;
  maxLength?: number;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    const next = draft.trim();
    if (next === '' || next === value) {
      setDraft(value);
      return;
    }
    onCommit(next);
  };

  return (
    <input
      type="text"
      className={className}
      placeholder={placeholder}
      maxLength={maxLength}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !isImeEnter(e)) {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
        if (e.key === 'Escape') {
          setDraft(value);
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

/** モーダルなどで Enter による確定を扱うときの判定。 */
export function shouldSubmitOnEnter(e: React.KeyboardEvent): boolean {
  return e.key === 'Enter' && !isImeEnter(e);
}
